import { useEffect, useRef } from 'preact/hooks';
import type { RefObject } from 'preact';

/**
 * 화면 안의 모든 내부 링크 클릭을 가로채 확인을 받는다.
 *
 * 영상 시청처럼 "지금 나가면 적립이 취소되는" 화면에서 쓴다. 탭바·헤더 아이콘처럼
 * 나중에 링크가 늘어나도 따로 손댈 필요가 없도록 컨테이너에서 한 번에 잡는다.
 *
 * ★ **캡처 단계**로 등록해야 한다. preact-router 는 document 에 버블 단계
 *   리스너를 달아 링크를 가로채므로, 캡처에서 stopPropagation 해야 라우터까지
 *   가지 않는다. 엘리먼트 자신의 onClick 보다도 먼저 실행된다.
 *
 * 외부 링크(http…), 새 탭(target=_blank), 페이지 내 앵커(#…)는 건드리지 않는다.
 */
export function useLeaveConfirm(
  containerRef: RefObject<HTMLElement | null>,
  /** 확인이 필요한 상태인지 */
  active: boolean,
  /** 나가도 되는지 묻는다. true 면 이동한다. */
  confirm: () => Promise<boolean>,
  /** 확인을 통과했을 때 실제 이동 */
  navigate: (href: string) => void
) {
  const state = useRef({ active, confirm, navigate });
  state.current = { active, confirm, navigate };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onClick = (e: MouseEvent) => {
      if (!state.current.active) return;
      // 새 탭으로 여는 조작(⌘/Ctrl/Shift/가운데 클릭)은 현재 화면을 떠나지 않는다.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;

      const anchor = (e.target as HTMLElement | null)?.closest?.(
        'a[href]'
      ) as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href') ?? '';
      if (!href.startsWith('/') || href.startsWith('//')) return; // 외부·프로토콜 상대 주소
      if (anchor.target && anchor.target !== '_self') return;

      e.preventDefault();
      e.stopPropagation();

      void (async () => {
        if (await state.current.confirm()) state.current.navigate(href);
      })();
    };

    el.addEventListener('click', onClick, true);
    return () => el.removeEventListener('click', onClick, true);
  }, [containerRef]);
}
